import React, { FC, useCallback } from 'react';
import { FormRenderProps, Form as ReactForm } from 'react-final-form';
import { Redirect, Link as RouterLink, useLocation } from 'react-router-dom';

import { Config } from 'final-form';

import { Button, Text } from '@nord/ui';

import LazyImage from '../../../../atoms/LazyImage';
import { FormError } from '../../../../molecules/Form/FormError';
import Input from '../../../../molecules/Form/Input';
import Submit from '../../../../molecules/Form/Submit';
import TwoColView from '../../../../templates/TwoColView';
import { isSubmitDisabled } from '../../../../tools/isSubmitDisabled';
import { IsEmail, Required, composeValidators } from '../../../../validation/FormValidation';
import forgotPasswordEmailLowResImg from '../../../assets/img/Forgot-password-email.png';
import forgotPasswordEmailImg from '../../../assets/img/Forgot-password-email.svg';
import forgotPasswordLowResImg from '../../../assets/img/Forgot-password.png';
import forgotPasswordImg from '../../../assets/img/Forgot-password.svg';

export interface IForgotPasswordValues {
  organizationIdentifier?: string;
  email?: string;
}

export interface IProps {
  isSubmitting?: boolean;
  onSubmit: Config<IForgotPasswordValues>['onSubmit'];
  loggedIn: boolean;
  isOrganizationRequired?: boolean;
}

const ForgotPassword: FC<IProps> = ({
  onSubmit,
  isSubmitting,
  loggedIn,
  isOrganizationRequired = false,
}) => {
  const location = useLocation<{ identifier?: string }>();
  const organizationIdentifier = location?.state?.identifier;
  let initialValues: IForgotPasswordValues = { email: '' };
  if (isOrganizationRequired) {
    initialValues = { ...initialValues, organizationIdentifier };
  }
  const content = useCallback(
    ({
      handleSubmit,
      submitSucceeded,
      values,
      ...restFormProps
    }: FormRenderProps<IForgotPasswordValues>) => {
      const img = submitSucceeded ? forgotPasswordEmailImg : forgotPasswordImg;
      const imgLowRes = submitSucceeded ? forgotPasswordEmailLowResImg : forgotPasswordLowResImg;
      const title = submitSucceeded ? 'Check your email!' : 'Forgot password?';
      const text = submitSucceeded ? (
        <>
          We have sent an email to <strong>{values.email}</strong> with password reset instructions.
        </>
      ) : (
        'Enter the email address associated with your account,' +
        ' and we’ll send you a link to reset your password.'
      );

      return (
        <TwoColView
          leftPanel={<LazyImage src={img} alt="Forgot password" preview={imgLowRes} />}
          backButton
        >
          <div className="md:mr-5">
            <Text
              className="mb-8px text-grey-tuna font-bold forget-password-header"
              variant="subtitle1"
            >
              {title}
            </Text>
            <Text className="mb-4" variant="body2">
              <span className="leading-24 text-grey-rolling-stone">{text}</span>
            </Text>
            {submitSucceeded ? (
              <Button
                data-testid="back-to-login"
                color="blue"
                className="w-full block text-center"
                component={RouterLink}
                to=""
              >
                Back to login
              </Button>
            ) : (
              <form noValidate autoComplete="off" onSubmit={handleSubmit}>
                {!organizationIdentifier && isOrganizationRequired && (
                  <Input name="organizationIdentifier" label="Organization ID" required />
                )}
                <Input
                  name="email"
                  type="email"
                  label="Email address"
                  placeholder="example@mail.com"
                  fieldProps={{ validate: composeValidators(Required, IsEmail) }}
                />
                <Submit
                  loading={isSubmitting}
                  className="w-full mb-4"
                  formProps={{ ...restFormProps }}
                  disabled={isSubmitting || isSubmitDisabled(restFormProps)}
                >
                  Send reset link
                </Submit>
                <FormError />
              </form>
            )}
          </div>
        </TwoColView>
      );
    },
    [isOrganizationRequired, organizationIdentifier, isSubmitting]
  );

  return loggedIn ? (
    <Redirect to="/" />
  ) : (
    <ReactForm onSubmit={onSubmit} initialValues={initialValues} render={content} />
  );
};

export default ForgotPassword;
